<template>
  <LoadingCard :loading="loading" class="overflow-visible">
    <!-- Header -->
    <div class="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-700">
      <div class="flex items-center gap-3 min-w-0">
        <div class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400">
          <svg class="h-4.5 w-4.5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M11.25 4.533A9.707 9.707 0 006 3a9.735 9.735 0 00-3.25.555.75.75 0 00-.5.707v14.25a.75.75 0 001 .707A8.237 8.237 0 016 18.75c1.995 0 3.823.707 5.25 1.886V4.533zM12.75 20.636A8.214 8.214 0 0118 18.75c.966 0 1.89.166 2.75.47a.75.75 0 001-.708V4.262a.75.75 0 00-.5-.707A9.735 9.735 0 0018 3a9.707 9.707 0 00-5.25 1.533v16.103z" />
          </svg>
        </div>
        <div class="min-w-0">
          <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">دوره‌های در انتظار تأیید کانون</h3>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">دوره‌های آموزشگاه‌های زیرمجموعه شما</p>
        </div>
      </div>

      <button
        @click="fetch()"
        :disabled="loading"
        class="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:text-primary-600 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
        title="بارگذاری مجدد"
      >
        <svg :class="['h-4 w-4', loading ? 'animate-spin' : '']" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      </button>
    </div>

    <!-- Empty -->
    <div v-if="!loading && courses.length === 0" class="flex flex-col items-center justify-center py-12 px-6 text-center">
      <p class="text-sm font-medium text-gray-600 dark:text-gray-300">هیچ دوره‌ای در انتظار تأیید نیست</p>
    </div>

    <!-- Course list -->
    <div v-else class="divide-y divide-gray-100 dark:divide-gray-700/60">
      <div
        v-for="course in courses"
        :key="course.id"
        @click="openCourse(course)"
        class="group flex items-center gap-3 px-4 sm:px-6 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
      >
        <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-xs font-semibold text-gray-500">
          {{ initial(course.title) }}
        </div>
        <div class="flex-1 min-w-0">
          <p class="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{{ course.title }}</p>
          <p v-if="course.institute" class="text-xs text-gray-400 truncate">{{ course.institute.name }}</p>
        </div>
        <span
          v-if="course.kanoon_pending_count > 0"
          class="shrink-0 inline-flex items-center rounded-md bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-400"
        >
          {{ toFa(course.kanoon_pending_count) }} در انتظار شما
        </span>
        <span
          v-else
          class="shrink-0 inline-flex items-center rounded-md bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-400"
        >
          بررسی شده
        </span>
      </div>
    </div>

    <!-- Pagination -->
    <div
      v-if="meta.last_page > 1"
      class="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-t border-gray-100 dark:border-gray-700"
    >
      <p class="text-xs text-gray-500 dark:text-gray-400">{{ toFa(meta.total) }} دوره</p>
      <div class="flex items-center gap-1">
        <button
          @click="goTo(meta.current_page - 1)"
          :disabled="meta.current_page <= 1 || loading"
          class="inline-flex h-8 items-center rounded-lg border border-gray-200 dark:border-gray-600 px-3 text-xs text-gray-600 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700/50"
        >
          قبلی
        </button>
        <span class="px-2 text-xs text-gray-500">
          {{ toFa(meta.current_page) }} / {{ toFa(meta.last_page) }}
        </span>
        <button
          @click="goTo(meta.current_page + 1)"
          :disabled="meta.current_page >= meta.last_page || loading"
          class="inline-flex h-8 items-center rounded-lg border border-gray-200 dark:border-gray-600 px-3 text-xs text-gray-600 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700/50"
        >
          بعدی
        </button>
      </div>
    </div>

    <!-- Detail modal -->
    <Modal v-if="selected" :show="true" size="3xl" @close-via-escape="closeCourse">
      <div class="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg max-h-[90vh] flex flex-col">
        <div class="flex items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-700 shrink-0">
          <div class="min-w-0">
            <h2 class="text-sm sm:text-base font-bold text-gray-900 dark:text-gray-100 truncate">
              {{ detail?.course?.title ?? selected.title }}
            </h2>
            <p v-if="detail?.course?.institute" class="text-xs text-gray-400 truncate mt-0.5">
              {{ detail.course.institute.name }}
            </p>
          </div>
          <button
            @click="closeCourse"
            class="shrink-0 h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Tabs: the verdict is given against the syllabus, so both live here -->
        <div class="flex items-center gap-1 px-4 sm:px-6 pt-3 border-b border-gray-100 dark:border-gray-700 shrink-0">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            type="button"
            @click="activeTab = tab.key"
            :class="[
              'inline-flex h-9 items-center rounded-t-lg px-3 text-xs font-medium transition-colors',
              activeTab === tab.key
                ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/40',
            ]"
          >
            {{ tab.label }}
          </button>
        </div>

        <div v-if="activeTab === 'content'" class="flex-1 overflow-y-auto p-4 sm:p-6">
          <KanoonCourseContent :course-id="selected.id" />
        </div>

        <div v-else class="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
          <div v-if="detailLoading" class="flex justify-center py-16 text-gray-400">
            <svg class="h-6 w-6 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>

          <p v-else-if="requests.length === 0" class="py-10 text-center text-sm text-gray-500">
            درخواستی برای بررسی باقی نمانده است.
          </p>

          <div
            v-for="request in requests"
            v-else
            :key="request.id"
            class="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
          >
            <div class="flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-700/40">
              <div class="min-w-0">
                <p class="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{{ request.model_label }}</p>
                <p class="text-xs text-gray-400 mt-0.5">{{ request.type_label }} · {{ request.submitted_at }}</p>
              </div>
              <div class="flex items-center gap-1.5 shrink-0">
                <span class="inline-flex items-center rounded-md bg-gray-200 dark:bg-gray-600 px-2 py-0.5 text-[11px] text-gray-700 dark:text-gray-200">
                  {{ request.publish_status_label }}
                </span>
                <span
                  v-if="request.kanoon_status_label"
                  :class="['inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium', kanoonBadgeClass(request.kanoon_status)]"
                >
                  کانون: {{ request.kanoon_status_label }}
                </span>
              </div>
            </div>

            <!-- Field diffs -->
            <div class="divide-y divide-gray-100 dark:divide-gray-700/60">
              <div v-for="(subject, index) in request.fields" :key="index" class="px-4 py-3">
                <p v-if="subject.subject_label" class="text-xs font-semibold text-gray-500 mb-2">
                  {{ subject.subject_label }}
                </p>
                <dl class="grid gap-2 sm:grid-cols-2">
                  <div v-for="field in subject.fields" :key="field.key" class="text-xs">
                    <dt class="text-gray-400 mb-0.5">{{ field.label }}</dt>
                    <dd class="text-gray-800 dark:text-gray-200 break-words">
                      <span v-if="field.original !== null && field.original !== field.new" class="text-red-500 line-through ml-1">
                        {{ display(field.original) }}
                      </span>
                      <span class="font-medium">{{ display(field.new) }}</span>
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            <!-- Kanoon verdict already given -->
            <div
              v-if="!request.can_act"
              class="px-4 py-3 bg-gray-50 dark:bg-gray-700/30 text-xs text-gray-500 dark:text-gray-400"
            >
              نظر شما در {{ request.kanoon_reviewed_at }} ثبت شده است.
              <span v-if="request.kanoon_rejection_reason"> دلیل: {{ request.kanoon_rejection_reason }}</span>
            </div>

            <!-- Actions -->
            <div v-else class="px-4 py-3 bg-gray-50 dark:bg-gray-700/30">
              <div v-if="rejectingId === request.id" class="space-y-2">
                <textarea
                  v-model="rejectReason"
                  rows="2"
                  placeholder="دلیل رد کردن را بنویسید..."
                  class="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-xs text-gray-800 dark:text-gray-200 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                ></textarea>
                <div class="flex items-center gap-2">
                  <button
                    @click="submitReject(request)"
                    :disabled="acting || rejectReason.trim().length === 0"
                    class="inline-flex h-8 items-center rounded-lg bg-red-500 px-3 text-xs font-medium text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-red-600"
                  >
                    ثبت رد
                  </button>
                  <button
                    @click="cancelReject"
                    :disabled="acting"
                    class="inline-flex h-8 items-center rounded-lg border border-gray-200 dark:border-gray-600 px-3 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    انصراف
                  </button>
                </div>
              </div>

              <div v-else class="flex items-center gap-2">
                <button
                  @click="approve(request)"
                  :disabled="acting"
                  class="inline-flex h-8 items-center rounded-lg bg-emerald-500 px-3 text-xs font-medium text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-emerald-600"
                >
                  تأیید کانون
                </button>
                <button
                  @click="startReject(request)"
                  :disabled="acting"
                  class="inline-flex h-8 items-center rounded-lg border border-red-200 dark:border-red-800 px-3 text-xs font-medium text-red-600 dark:text-red-400 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  رد کردن
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  </LoadingCard>
</template>

<script>
export default {
  name: 'KanoonCourseApprovalCard',

  props: {
    card: { type: Object, required: true },
  },

  data: () => ({
    loading: true,
    courses: [],
    meta: { current_page: 1, last_page: 1, total: 0 },
    selected: null,
    detail: null,
    detailLoading: false,
    acting: false,
    rejectingId: null,
    rejectReason: '',
    activeTab: 'requests',
  }),

  computed: {
    endpoint() {
      return this.card?.apiEndpoint ?? '/dashboard-api/kanoon/course-approvals'
    },

    requests() {
      return this.detail?.requests ?? []
    },

    tabs() {
      return [
        { key: 'requests', label: 'درخواست‌های در انتظار' },
        { key: 'content', label: 'جزئیات و سرفصل دوره' },
      ]
    },
  },

  created() {
    this.fetch()
  },

  methods: {
    initial(name) {
      return (name ?? '').trim().charAt(0) || '?'
    },

    toFa(value) {
      if (value == null) return '۰'
      return Number(value).toLocaleString('fa-IR')
    },

    display(value) {
      if (value === null || value === undefined || value === '') return '—'
      if (typeof value === 'boolean') return value ? 'بله' : 'خیر'
      if (typeof value === 'object') return JSON.stringify(value)
      return value
    },

    kanoonBadgeClass(status) {
      const map = {
        approved: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
        rejected: 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400',
      }
      return map[status] ?? 'bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300'
    },

    goTo(page) {
      if (page < 1 || page > this.meta.last_page) return
      this.fetch(page)
    },

    async fetch(page = null) {
      this.loading = true
      try {
        const { data } = await Nova.request().get(this.endpoint, {
          params: { page: page ?? this.meta.current_page },
        })
        this.courses = data.courses ?? []
        this.meta = data.meta ?? this.meta
      } catch (error) {
        this.courses = []
        Nova.error(error?.response?.data?.message ?? 'دریافت لیست دوره‌ها با خطا مواجه شد.')
      } finally {
        this.loading = false
      }
    },

    async openCourse(course) {
      this.selected = course
      this.detail = null
      this.activeTab = 'requests'
      this.cancelReject()
      await this.loadDetail()
    },

    closeCourse() {
      this.selected = null
      this.detail = null
      this.activeTab = 'requests'
      this.cancelReject()
    },

    async loadDetail() {
      const courseId = this.selected?.id
      if (!courseId) return

      this.detailLoading = true
      try {
        const { data } = await Nova.request().get(`${this.endpoint}/${courseId}`)
        this.detail = data
      } catch (error) {
        Nova.error(error?.response?.data?.message ?? 'دریافت جزئیات دوره با خطا مواجه شد.')
        this.closeCourse()
      } finally {
        this.detailLoading = false
      }
    },

    startReject(request) {
      this.rejectingId = request.id
      this.rejectReason = ''
    },

    cancelReject() {
      this.rejectingId = null
      this.rejectReason = ''
    },

    async approve(request) {
      await this.act(() => Nova.request().post(`${this.endpoint}/requests/${request.id}/approve`))
    },

    async submitReject(request) {
      await this.act(() =>
        Nova.request().post(`${this.endpoint}/requests/${request.id}/reject`, {
          reason: this.rejectReason.trim(),
        })
      )
    },

    async act(operation) {
      this.acting = true
      try {
        const { data } = await operation()
        Nova.success(data?.message ?? 'ثبت شد.')
        this.cancelReject()
        await this.loadDetail()
        await this.fetch()
      } catch (error) {
        Nova.error(error?.response?.data?.message ?? 'ثبت نظر با خطا مواجه شد.')
      } finally {
        this.acting = false
      }
    },
  },
}
</script>
